import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import logger from '../utils/logging/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for session expired flag first
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');
    if (sessionExpired === 'true') {
      // Clear user data but keep the session expired flag for login page to display
      localStorage.removeItem('faithinsight_user');
      localStorage.removeItem('faithinsight_token');
      setUser(null);
      setLoading(false);
      return;
    }

    // Check for existing user session
    const savedUser = localStorage.getItem('faithinsight_user');
    const savedToken = localStorage.getItem('faithinsight_token');
    
    if (savedUser && savedToken) {
      try {
        setUser(JSON.parse(savedUser));
        // Don't validate token immediately - let the app try to use it
        // If it's invalid, the API calls will handle it
        setLoading(false);
      } catch (error) {
        logger.error('Error parsing saved user', { error });
        localStorage.removeItem('faithinsight_user');
        localStorage.removeItem('faithinsight_token');
        localStorage.removeItem('faithinsight_refresh_token');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);


  // Shared by every successful-auth response shape (password login, OTP
  // login, ...): apply the user/tokens to state + localStorage the same way
  // regardless of which sign-in method produced them.
  const applyAuthSession = (response) => {
    const user = response.user;
    setUser(user);
    localStorage.setItem('faithinsight_user', JSON.stringify(user));
    localStorage.setItem('faithinsight_token', response.token);
    // Store refresh token if provided
    if (response.refreshToken) {
      localStorage.setItem('faithinsight_refresh_token', response.refreshToken);
    }

    // FIX: Clear session expired and logged out flags on successful login
    // This prevents stale flags from causing immediate logout after login
    localStorage.removeItem('faithinsight_session_expired');
    localStorage.removeItem('faithinsight_logged_out');

    return {
      success: true,
      requiresPasswordChange: Boolean(response.requiresPasswordChange)
    };
  };

  const login = async (email, password) => {
    try {
      // Use real API
      const response = await apiService.login({ email, password });

      if (response.success) {
        return applyAuthSession(response);
      } else {
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error) {
      logger.error('Login error', { error });
      // Pass through the actual error message from the API (e.g., "Invalid credentials")
      return { success: false, error: error.message || 'Login failed. Please check your credentials.' };
    }
  };

  const requestOtp = async (email) => {
    try {
      const response = await apiService.requestOtp(email);
      return { success: Boolean(response.success), message: response.message };
    } catch (error) {
      logger.error('OTP request error', { error });
      return { success: false, error: error.message || 'Failed to request code. Please try again.' };
    }
  };

  const loginWithOtp = async (email, code) => {
    try {
      const response = await apiService.verifyOtp(email, code);

      if (response.success) {
        return applyAuthSession(response);
      } else {
        return { success: false, error: response.error || 'Invalid or expired code' };
      }
    } catch (error) {
      logger.error('OTP login error', { error });
      return { success: false, error: error.message || 'Invalid or expired code' };
    }
  };

  const signup = async (email, password, firstName, lastName, phone) => {
    try {
      // Use real API
      const response = await apiService.signup({ email, password, firstName, lastName, phone });
      
      if (response.success) {
        const user = response.user;
        setUser(user);
        localStorage.setItem('faithinsight_user', JSON.stringify(user));
        localStorage.setItem('faithinsight_token', response.token);
        // Store refresh token if provided
        if (response.refreshToken) {
          localStorage.setItem('faithinsight_refresh_token', response.refreshToken);
        }
        
        // FIX: Clear session expired and logged out flags on successful signup
        // This prevents stale flags from causing immediate logout after signup
        localStorage.removeItem('faithinsight_session_expired');
        localStorage.removeItem('faithinsight_logged_out');
        
        return { success: true };
      } else {
        return { success: false, error: response.error || 'Signup failed' };
      }
    } catch (error) {
      logger.error('Signup error', { error });
      return { success: false, error: 'Signup failed. Please try again.' };
    }
  };

  const logout = async () => {
    // Set logged out flag FIRST to ensure it's set even if API call fails
    // This prevents the session expired message from showing on manual logout
    localStorage.setItem('faithinsight_logged_out', 'true');
    
    // Clear session expired flag if it exists (from previous session expiration)
    localStorage.removeItem('faithinsight_session_expired');
    
    try {
      // Try to call logout API
      await apiService.logout();
      // CSRF token is cleared in apiService.logout()
    } catch (error) {
      logger.debug('Logout API call failed, continuing with local logout', { error });
    }
    
    setUser(null);
    localStorage.removeItem('faithinsight_user');
    localStorage.removeItem('faithinsight_token');
    localStorage.removeItem('faithinsight_refresh_token');
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('faithinsight_user', JSON.stringify(updatedUser));
  };

  const markPasswordChanged = () => {
    if (user) {
      const updatedUser = { ...user, password_change_required: false };
      setUser(updatedUser);
      localStorage.setItem('faithinsight_user', JSON.stringify(updatedUser));
    }
  };

  // Helper function to decode JWT token
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Proactive token refresh - check and refresh token before expiration
  // NOTE: This runs continuously, including during active quizzes, to prevent session expiration
  // For quizzes: refreshes when within 30 minutes of expiration (more aggressive)
  // For regular pages: refreshes when within 5 minutes of expiration
  useEffect(() => {
    if (!user) return;

    let timeoutId = null;
    let intervalId = null;

    // Helper to check if user is on a quiz page
    const isOnQuizPage = () => {
      return window.location.pathname.includes('/student/attempt/');
    };

    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('faithinsight_token');
      const refreshToken = localStorage.getItem('faithinsight_refresh_token');
      
      if (!token || !refreshToken) return;

      const decoded = decodeToken(token);
      if (!decoded || !decoded.exp) return;

      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      
      // More aggressive refresh during quiz sessions (30 minutes before expiration)
      // Regular refresh for other pages (5 minutes before expiration)
      const isQuiz = isOnQuizPage();
      const refreshThreshold = isQuiz ? 30 * 60 * 1000 : 5 * 60 * 1000; // 30 min for quiz, 5 min for regular
      
      // If token is expired or within refresh threshold, attempt to refresh
      // IMPORTANT: Also refresh if token is already expired (timeUntilExpiry <= 0)
      // This handles cases where token expires between checks
      if (timeUntilExpiry < refreshThreshold) {
        try {
          const statusText = timeUntilExpiry <= 0 
            ? 'EXPIRED' 
            : `expires in ${Math.round(timeUntilExpiry / 1000)} seconds`;
          logger.debug(`Proactively refreshing token (${statusText})${isQuiz ? ' [QUIZ MODE - aggressive refresh]' : ''}`, { timeUntilExpiry, isQuiz });
          const response = await apiService.refreshToken();
          
          if (response.success && response.token) {
            localStorage.setItem('faithinsight_token', response.token);
            if (response.user) {
              setUser(response.user);
              localStorage.setItem('faithinsight_user', JSON.stringify(response.user));
            }
            logger.debug('Token refreshed proactively');
            // After refresh, schedule next check with new token
            scheduleNextCheck();
          }
        } catch (error) {
          // Only log as error if it's not a token expiration (expected scenario)
          const isExpectedError = error?.message?.includes('expired') || 
                                  error?.message?.includes('Token expired') ||
                                  error?.refreshTokenExpired;
          if (isExpectedError) {
            logger.debug('Token refresh failed (expected - token expired)', { error: error.message, isQuiz });
          } else {
            logger.error('Failed to refresh token proactively', { error, isQuiz });
          }
          // Don't logout here - let the next API call handle it
          // This ensures quiz operations can continue even if refresh fails
          // Schedule retry sooner during quiz sessions
          const retryDelay = isQuiz ? 30000 : 60000; // 30 seconds for quiz, 1 minute for regular
          timeoutId = setTimeout(scheduleNextCheck, retryDelay);
        }
      } else if (timeUntilExpiry > refreshThreshold) {
        // Token not close to expiration - schedule check for when we're within refresh threshold
        const timeUntilRefreshWindow = timeUntilExpiry - refreshThreshold;
        timeoutId = setTimeout(scheduleNextCheck, timeUntilRefreshWindow);
      }
    };

    const scheduleNextCheck = () => {
      const token = localStorage.getItem('faithinsight_token');
      const refreshToken = localStorage.getItem('faithinsight_refresh_token');
      
      if (!token || !refreshToken) return;

      const decoded = decodeToken(token);
      if (!decoded || !decoded.exp) return;

      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      
      const isQuiz = isOnQuizPage();
      const refreshThreshold = isQuiz ? 30 * 60 * 1000 : 5 * 60 * 1000;
      const checkInterval = isQuiz ? 30000 : 60000; // Check every 30 seconds during quiz, 1 minute otherwise
      
      // IMPORTANT: Don't return early if token is expired - we still want to attempt refresh
      // This ensures we catch expired tokens and refresh them
      if (timeUntilExpiry < refreshThreshold) {
        // Token is expired or close to expiration - check more frequently
        // During quiz: check every 30 seconds, otherwise every minute
        timeoutId = setTimeout(checkAndRefreshToken, checkInterval);
      } else {
        // Schedule check for when we're within refresh threshold
        const timeUntilRefreshWindow = timeUntilExpiry - refreshThreshold;
        timeoutId = setTimeout(checkAndRefreshToken, timeUntilRefreshWindow);
      }
    };

    // During quiz sessions, also set up a periodic check to detect when user navigates to quiz
    // This ensures we switch to aggressive refresh mode immediately when entering a quiz
    if (isOnQuizPage()) {
      // Check every 10 seconds during quiz to ensure we're always in aggressive mode
      intervalId = setInterval(() => {
        checkAndRefreshToken();
      }, 10000);
    }

    // FIX: Handle tab visibility changes - refresh token immediately when tab becomes visible
    // This handles cases where the browser suspended the tab (mobile, laptop sleep, etc.)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logger.debug('Tab became visible - checking token status');
        // Immediately check and refresh token when tab becomes visible
        checkAndRefreshToken();
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // FIX: Also handle page focus (some browsers don't fire visibilitychange reliably)
    const handleFocus = () => {
      if (isOnQuizPage()) {
        logger.debug('Window focused during quiz - checking token status');
        checkAndRefreshToken();
      }
    };
    
    window.addEventListener('focus', handleFocus);

    // Check immediately and schedule next check
    checkAndRefreshToken();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    requestOtp,
    loginWithOtp,
    signup,
    logout,
    updateUser,
    setUser,
    markPasswordChanged,
    decodeToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
